/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, March 30th 2024, 12:35:14 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { split } from "lodash";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ImageIcon from "@mui/icons-material/Image";

/** React Cropper */
import Cropper from "react-cropper";
import CropperType from "cropperjs";
import "cropperjs/dist/cropper.css";
import { OSLoadingButton } from "ui-widgets/buttons/Buttons";

export interface ImageCroppingState {
  cropper: CropperType;
  isCroppingImage: boolean;
  isSuccessfulCrop: boolean;
}

type ImageCropperProps = {
  imageToCropURL: string;
  imageToCropName: string;
  onCropImage: (croppedImage: File) => void;
  onCancelCrop?: () => void;
  children: (args: ImageCroppingState) => React.ReactNode;
};

export const ImageCropper = ({
  onCancelCrop,
  onCropImage,
  children,
  imageToCropName,
  imageToCropURL,
}: ImageCropperProps) => {
  const [imageCroppingState, setImageCroppingState] = React.useState<ImageCroppingState>({
    cropper: null,
    isCroppingImage: false,
    isSuccessfulCrop: false,
  });

  const { cropper, isCroppingImage } = imageCroppingState;

  const getCropData = () => {
    if (typeof cropper !== "undefined") {
      setImageCroppingState((prev) => ({ ...prev, isCroppingImage: true, isSuccessfulCrop: false }));

      const croppedImageDataURL = cropper.getCroppedCanvas().toDataURL();
      const nameWithouExtension = split(imageToCropName, ".")[0];
      const croppedFileName = `${nameWithouExtension}.png`;

      fetch(croppedImageDataURL)
        .then((res) => res.blob())
        .then((blob) => {
          const croppedImageFile = new File([blob], croppedFileName, { type: "image/png" });
          onCropImage(croppedImageFile);
          setImageCroppingState((prev) => ({ ...prev, isCroppingImage: false, isSuccessfulCrop: true }));
        });
    }
  };

  return (
    <>
      {imageToCropURL ? (
        <Stack spacing={2}>
          <Cropper
            zoomTo={0.5}
            initialAspectRatio={1}
            // preview=".img-preview"
            src={imageToCropURL}
            viewMode={0}
            minCropBoxHeight={10}
            minCropBoxWidth={10}
            background={false}
            responsive={true}
            autoCropArea={1}
            checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
            onInitialized={(instance) => {
              setImageCroppingState((prev) => ({ ...prev, cropper: instance }));
            }}
            guides={true}
          />
          <Stack spacing={2} direction="row" sx={{ alignItems: "center", justifyContent: "center" }}>
            <Button variant="text" onClick={onCancelCrop}>
              Cancel
            </Button>
            <OSLoadingButton
              isLoading={isCroppingImage}
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={getCropData}
            >
              Crop Image
            </OSLoadingButton>
          </Stack>
        </Stack>
      ) : (
        children(imageCroppingState)
      )}
    </>
  );
};
